import type { IdleAgent } from '../transport/api'

/**
 * THE NUMBER ON THE APP ICON: how many AGENTS have something unread.
 *
 * Owner, 2026-09-21, relayed by avg-devapp-appstore: 有幾個 agent 還沒有被讀取資訊
 * 為主…現在在 App 裡面有三個 agent 還沒有被讀取到他最新的訊息。那麼小紅點在 App icon
 * 上面就應該要顯示為三.
 *
 * AGENTS, NOT MESSAGES, and that is the whole point of a separate function. The
 * number already on the nav bar is `activityUnread`, which SUMS every unread
 * message and folds in projects — three agents holding two messages each reads as
 * 6 there and must read as 3 here. Two different questions that both deserve a
 * number; the mistake would be to reuse one for the other because both are called
 * "unread".
 *
 * It counts exactly the rows the rail draws a dot on — `AgentRowView` lights
 * `.sb-unread` from `idle.byKey.get(idleKey(a))?.unread > 0`, and this counts the
 * same map the same way — so the icon can never disagree with what he sees when he
 * opens the app. That equality is the contract, not an implementation detail:
 * anything that changes which rows get a dot has to change this too.
 *
 * ACROSS ALL MACHINES, because `idle.byKey` is the everywhere map, keyed by
 * (device, host, session). An agent is one entry however many machines are linked.
 *
 * NOT projects or DMs. His sentence says agents, and avg-devapp-appstore asked the
 * question explicitly rather than assuming. Widening it later is a one-line change
 * plus a rename; guessing wide now would put a number on his home screen that no
 * sentence of his asked for.
 */
export function agentsWithUnread(rows: Iterable<IdleAgent>): number {
  let n = 0
  for (const a of rows) if ((a.unread ?? 0) > 0) n++
  return n
}

/**
 * The native shell's badge bridge, or nothing.
 *
 * avg-devapp-appstore's `AppBadge.swift`: a WKScriptMessageHandler named
 * `avengentsBadge` taking `{count}`, clamping 0..999, ignoring duplicates, and
 * clearing the icon on 0. The Mac shell will answer the same name with its Dock
 * tile, so one call serves both and this file needs no per-platform branch.
 *
 * FEATURE-DETECTED, because the handler does not exist in the 1.0 store build that
 * is on real phones right now, and will not exist in a browser at all. A missing
 * handler is the NORMAL case for most of this app's surfaces, not an error — so
 * this returns false and says nothing rather than logging a failure on every web
 * load.
 *
 * Wrapped in try/catch for the same reason every other bridge call in this codebase
 * is: `window.webkit` is a foreign object, postMessage can throw on a shell that
 * registered the name and then tore the handler down, and a throw here would unmount
 * the effect that calls it.
 */
type BadgeBridge = { postMessage: (m: { count: number }) => void }

export function badgeBridge(): BadgeBridge | null {
  try {
    const w = window as unknown as {
      webkit?: { messageHandlers?: Record<string, BadgeBridge | undefined> }
    }
    return w.webkit?.messageHandlers?.avengentsBadge ?? null
  } catch {
    return null
  }
}

/** Returns whether the number actually reached a shell — used by the tests, and honest. */
export function postAppBadge(count: number): boolean {
  const bridge = badgeBridge()
  if (!bridge) return false
  try {
    // Clamped here as well as natively. Not distrust of the shell: a NaN from a
    // future caller would be posted as `{"count":null}` and the native side would
    // log "a message without a count" and keep the OLD number on the icon, which is
    // the one failure mode a badge must never have — a stale number nobody can see
    // is wrong about.
    const n = Number.isFinite(count) ? Math.max(0, Math.min(Math.floor(count), 999)) : 0
    bridge.postMessage({ count: n })
    return true
  } catch {
    return false
  }
}
